import { useQueries } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { EncounterLabRequisitions } from '@/types/scheduling-api';

/**
 * PROD-2846: `GET /api/Patients/{patientId}/Encounters/{encounterId}/LabRequisitions`
 * (Scrips.Patient #1022, PROD-2841). The organization comes from the token.
 *
 * The lab desk asks only for the encounters on the current page that have
 * at least one row with a `requisitionId`, so legacy pages (and every page
 * before AddLabRequisitions.sql has run) make no extra calls.
 */
export interface EncounterKey {
  patientId: string;
  encounterId: string;
}

export const encounterLabRequisitionsKey = (k: EncounterKey) =>
  ['lab-requisitions', k.patientId, k.encounterId] as const;

export const encounterLabRequisitionsPath = (k: EncounterKey) =>
  `/api/Patients/${encodeURIComponent(k.patientId)}/Encounters/${encodeURIComponent(
    k.encounterId,
  )}/LabRequisitions`;

/** Map of encounterId → requisitions (missing while loading or on error). */
export function useEncounterLabRequisitions(
  encounters: EncounterKey[],
): Map<string, EncounterLabRequisitions> {
  const results = useQueries({
    queries: encounters.map((k) => ({
      queryKey: encounterLabRequisitionsKey(k),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        get<EncounterLabRequisitions>(encounterLabRequisitionsPath(k), { signal }),
      enabled: !!k.patientId && !!k.encounterId,
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: false,
    })),
  });
  const map = new Map<string, EncounterLabRequisitions>();
  results.forEach((r, i) => {
    if (r.data) map.set(encounters[i].encounterId, r.data);
  });
  return map;
}
